package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initSmtpknPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("smtpkn.md", FrontMatterElement.Builder { addScalar("title", "smtpkn"); addScalar("searchTitle", "SMTP client for Kotlin/Native — RFC 5321, STARTTLS, SASL"); addScalar("description", "SMTP written from the specification so a Kotlin/Native service can send mail with no JVM in sight. TLS with the chain actually verified, seven SASL mechanisms."); addScalar("library", "smtpkn"); addScalar("post", "/blog/smtp-from-the-spec"); }))
}

@Page("/smtpkn")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun SmtpknPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the JVM this was solved twenty years ago. On Kotlin/Native there is nothing, and the two workarounds fail the same way: shelling out to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sendmail")
        }
        org.jetbrains.compose.web.dom.Text(" and wrapping libcurl both hide the protocol exactly where it has to be visible.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-decisions-worth-knowing-about") }) {
        org.jetbrains.compose.web.dom.Text("Three decisions worth knowing about")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Replies are matched by counting.")
        }
        org.jetbrains.compose.web.dom.Text(" The specification forbids matching by code or text, and it is right to: codes repeat and text is server-defined. Only order holds. With ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PIPELINING")
        }
        org.jetbrains.compose.web.dom.Text(" negotiated and five commands in flight, a matcher that reads codes pairs the wrong reply with the wrong command exactly when the session is busiest — silently.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A partial refusal is a result, not an exception.")
        }
        org.jetbrains.compose.web.dom.Text(" Three recipients accepted, two rejected: if that throws, the caller has lost which three worked, and the only safe options left are to give up or to retry all five and deliver three duplicates.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Anything a rule forbids is refused, not sanitised.")
        }
        org.jetbrains.compose.web.dom.Text(" A line break inside an address is a way to append headers of somebody else's choosing. Refusing means the caller finds out where the mistake was made.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Submission per RFC 6409: port 587 with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("STARTTLS")
            }
            org.jetbrains.compose.web.dom.Text(", port 465 with implicit TLS.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("TLS with the certificate chain ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("and the host name")
            }
            org.jetbrains.compose.web.dom.Text(" verified — OpenSSL on Kotlin/Native, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SSLEngine")
            }
            org.jetbrains.compose.web.dom.Text(" on the JVM.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Seven SASL mechanisms: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("PLAIN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("LOGIN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CRAM-MD5")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SCRAM-SHA-1")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SCRAM-SHA-256")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("XOAUTH2")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("OAUTHBEARER")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("ESMTP: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("PIPELINING")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIZE")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("8BITMIME")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SMTPUTF8")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("DSN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ENHANCEDSTATUSCODES")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CHUNKING")
            }
            org.jetbrains.compose.web.dom.Text(", plus punycode for internationalised domains.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Message building: RFC 5322 headers, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("multipart/alternative")
            }
            org.jetbrains.compose.web.dom.Text(", attachments, encoded words.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies { implementation("io.github.youndie:smtp-client:<VERSION>") }
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" is the platform this is built for and the only one it is claimed to work on — that is where the whole suite runs, including TLS against a real server and end to end against Mailpit and Postfix.")
    }
}
